import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  Lock,
  Eye,
  Activity,
  Clock,
} from "lucide-react";
import StudioGantt, { StudioConflictFeed } from "@/components/studio/StudioGantt";
import { studioApi } from "@/lib/api";

const formatExpiry = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const minutesUntil = (iso) => {
  if (!iso) return -1;
  return (new Date(iso).getTime() - Date.now()) / 60000;
};

export default function StudioPublicSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    studioApi
      .getPublicResources(token)
      .then((resp) => { if (mounted) setData(resp); })
      .catch((e) => {
        if (!mounted) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 410) setError({ kind: "expired", detail });
        else if (status === 404) setError({ kind: "not_found", detail });
        else setError({ kind: "unknown", detail: detail || "Failed to load" });
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const peopleById = useMemo(() => {
    const m = {};
    (data?.people || []).forEach((p) => { m[p.user_id] = p; });
    return m;
  }, [data?.people]);

  const expiringSoon = useMemo(() => {
    if (!data?.link?.expires_at) return false;
    const m = minutesUntil(data.link.expires_at);
    return m > 0 && m < 60 * 24; // < 24 hours left
  }, [data?.link?.expires_at]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    const titleByKind = {
      expired: "This link has expired",
      not_found: "Link not found",
      unknown: "Could not load link",
    };
    const bodyByKind = {
      expired: "The producer who created this snapshot will need to generate a fresh link.",
      not_found: "Check the URL — the token may be incomplete or revoked.",
      unknown: error.detail || "Try again in a moment.",
    };
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-md border border-zinc-800 bg-zinc-950/80 p-10 text-center">
          <Lock className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-light text-zinc-100 mb-2">
            {titleByKind[error.kind]}
          </h1>
          <p className="text-sm text-zinc-500" data-testid="share-error">
            {bodyByKind[error.kind]}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="studio-public-page">
      {/* ── Read-only banner ─────────────────────────────────── */}
      <div className="border-b border-amber-900/60 bg-amber-950/30 px-6 py-2.5">
        <div className="max-w-screen-2xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs">
            <Eye className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-overline text-amber-300" data-testid="share-badge">
              Read-only snapshot
            </span>
            <span className="text-zinc-500 font-mono">·</span>
            <span className="text-amber-100 font-medium">{data.link.name}</span>
          </div>
          <div className="flex items-center gap-4 text-[11px] font-mono text-amber-200/80">
            <div className="inline-flex items-center gap-1.5" title="Live data, recomputed at fetch time">
              <Activity className="w-3 h-3" />
              Generated {formatExpiry(data.generated_at)}
            </div>
            <div
              className={`inline-flex items-center gap-1.5 ${expiringSoon ? "text-rose-300" : ""}`}
              data-testid="share-expiry"
              title={data.link.expires_at}
            >
              <Clock className="w-3 h-3" />
              Expires {formatExpiry(data.link.expires_at)}
              {expiringSoon && <AlertTriangle className="w-3 h-3 ml-0.5" />}
            </div>
          </div>
        </div>
      </div>

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-8 pb-4">
        <div className="text-overline text-amber-500/80 mb-2">Studio</div>
        <h2 className="font-heading text-3xl font-light text-zinc-100">
          Resource Snapshot
        </h2>
        <p className="text-sm text-zinc-500 mt-2 max-w-xl">
          {data.range.start} → {data.range.end} · {data.projects.length} project
          {data.projects.length === 1 ? "" : "s"} · {data.people.length} crew
          {" · "}cap {data.capacity_hours_per_day}h/day
        </p>
      </div>

      {/* ── Stat strip ───────────────────────────────────────── */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <PublicStat label="Assigned People" value={data.people.length} />
          <PublicStat label="Active Projects" value={data.projects.length} dot="bg-amber-500" />
          <PublicStat label="Tasks in Window" value={data.tasks.length} dot="bg-teal-500" />
          <PublicStat
            label="Conflict Days"
            value={data.conflicts.length}
            warn={data.conflicts.length > 0}
            dot={data.conflicts.length === 0 ? "bg-emerald-500" : null}
          />
        </div>
      </div>

      {/* ── Body — read-only gantt + conflict feed ──────────── */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-10">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
          <StudioGantt
            people={data.people}
            tasks={data.tasks}
            conflicts={data.conflicts}
            rangeStart={data.range.start}
            rangeEnd={data.range.end}
            capacity={data.capacity_hours_per_day}
            allocations={data.allocations}
          />
          <div className="space-y-3">
            <PublicProjectLegend projects={data.projects} />
            <StudioConflictFeed
              conflicts={data.conflicts}
              peopleById={peopleById}
              onResolve={null}
            />
          </div>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────── */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-8 pt-2 text-center text-[10px] font-mono text-zinc-600">
        Whiplash Film & Creative · Powered by the Studio Resource Manager
      </div>
    </div>
  );
}

function PublicStat({ label, value, dot, warn }) {
  return (
    <div
      data-testid={`public-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-md border p-4 ${
        warn ? "border-orange-900/60 bg-orange-950/20" : "border-zinc-800 bg-zinc-950/60"
      }`}
    >
      <div className="flex items-center gap-2 text-overline mb-2">
        {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
        {warn && <AlertTriangle className="w-3 h-3 text-orange-400" />}
        <span className={warn ? "text-orange-400" : "text-zinc-500"}>{label}</span>
      </div>
      <div className={`font-mono text-2xl ${warn ? "text-orange-300" : "text-zinc-100"}`}>
        {value}
      </div>
    </div>
  );
}

function PublicProjectLegend({ projects }) {
  if (!projects?.length) return null;
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="text-overline text-zinc-500 mb-2">Project Palette</div>
      <ul className="space-y-1.5">
        {projects.map((p) => (
          <li key={p.id} className="flex items-center gap-2 text-xs">
            <span
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: p.color?.bar || "#71717a" }}
            />
            <span className="text-zinc-300 truncate" title={p.name}>{p.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
